import React, { useEffect, useState } from "react";

import PropTypes from "prop-types";
import hexWithOpacity from "core-web/utils/hexWithOpacity";
import styleObjectToProps from "core-web/utils/styleObjectToProps";
import styled from "theme/libs/styled";

const Wrapper = styled("div")`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;

    &[data-is-disabled="true"] {
        background-color: ${({ theme }) => theme.colors.darkGreige};
        opacity: 0.6;
        cursor: not-allowed;

        & > input {
            pointer-events: none;
        }
    }

    &[data-has-error="true"] {
        border-color: ${({ theme }) => theme.colors.red};
    }
`;

const Label = styled("label")`
    position: absolute;
    user-select: none;
    pointer-events: none;

    &[data-has-placeholder="false"] {
        cursor: text;
        top: 50%;
        transform: translateY(-50%);
        transition: all ${({ theme }) => theme.transitions.primary};
        left: 8px;
        color: ${({ theme }) => hexWithOpacity(theme.colors.black, 60)};
    }

    &[data-has-placeholder="true"] {
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        width: 1px;
        overflow: hidden;
        white-space: nowrap;
    }

    &[data-is-required="true"]::after {
        content: "*";
        display: block;
        position: absolute;
        top: 0;
        right: -7px;
        opacity: 1;
        transition: opacity ${({ theme }) => theme.transitions.primary};
    }

    &[data-has-error="true"] {
        color: ${({ theme }) => theme.colors.red};
        top: 4px;
        font-size: 10px;
        transform: translateY(0);
    }
`;

const Input = styled("input")`
    width: 100%;
    text-decoration: none;
    border: none;
    border-radius: 0;
    font-size: 16px;
    background-color: transparent;
    -webkit-appearance: none;
    color: ${({ theme }) => theme.colors.black};
    padding: 16px 14px 6px;
    transition: padding ${({ theme }) => theme.transitions.primary};

    &[data-has-placeholder="true"] {
        padding: 16px 14px;
    }

    &:focus,
    &:not(:placeholder-shown) {
        outline: none;
        & + label {
            top: 4px;
            left: 14px;
            font-size: 10px;
            transform: translateY(0);

            &::after {
                opacity: 0;
            }
        }
    }

    &::placeholder {
        display: none;
    }

    &[data-has-label="false"] {
        padding: 8px;

        & + label::after {
            display: none;
        }

        &::placeholder {
            display: initial;
        }
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
        box-shadow: 0 0 0 30px ${({ theme }) => theme.colors.lightGreige} inset !important;
    }
`;

const TextInput = ({
    autoComplete,
    ariaLabel,
    children,
    defaultValue,
    isDisabled,
    error,
    countryCode,
    showErrorStyle,
    handleBlur,
    handleChange,
    label,
    id,
    name,
    isRequired,
    type,
    placeholder,
    pattern,
    minLength,
    maxLength,
    customValue,
    usesCustomValue,
    ...rest
}) => {
    const [value, setValue] = useState(defaultValue || "");
    const fontStyles = styleObjectToProps("Myriad Pro/16-100");
    const { message: errorMessage } = error || {};

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    const hasError = !!errorMessage || showErrorStyle;

    const change = (e) => {
        if (isDisabled) {
            e.preventDefault();
        } else {
            if (!usesCustomValue) {
                setValue(e.target.value);
            }
            handleChange && handleChange(e);
        }
    };

    const blur = (e) => {
        if (!e.target.value) {
            setValue("");
        }
        handleBlur(e);
    };

    return (
        <Wrapper data-has-error={hasError} data-is-disabled={isDisabled} {...rest}>
            <Input
                data-has-label={!!label || !!errorMessage}
                data-has-placeholder={!!placeholder}
                data-locale={countryCode}
                autoComplete={autoComplete}
                id={id || name}
                name={name}
                type={type}
                pattern={pattern}
                aria-label={ariaLabel || label}
                required={isRequired}
                value={customValue || value}
                placeholder={placeholder || " "}
                minLength={minLength}
                maxLength={maxLength}
                onChange={change}
                onBlur={blur}
                {...fontStyles}
            />
            <Label
                htmlFor={name}
                data-is-required={isRequired}
                data-has-error={hasError}
                data-has-placeholder={!!placeholder}
                {...fontStyles}
            >
                {errorMessage || label}
            </Label>
            {children && children}
        </Wrapper>
    );
};

TextInput.propTypes = {
    ariaLabel: PropTypes.string,
    autoComplete: PropTypes.string,
    children: PropTypes.node,
    countryCode: PropTypes.string,
    customValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValue: PropTypes.string,
    error: PropTypes.exact({
        message: PropTypes.string,
        name: PropTypes.string,
    }),
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func,
    id: PropTypes.string,
    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    label: PropTypes.string.isRequired,
    maxLength: PropTypes.string,
    minLength: PropTypes.string,
    name: PropTypes.string.isRequired,
    pattern: PropTypes.string,
    placeholder: PropTypes.string,
    showErrorStyle: PropTypes.bool,
    type: PropTypes.string,
    usesCustomValue: PropTypes.bool,
};

TextInput.defaultProps = {
    ariaLabel: null,
    autoComplete: "on",
    children: null,
    countryCode: null,
    customValue: "",
    defaultValue: "",
    error: {
        message: "",
        name: "",
    },
    handleBlur: () => {},
    handleChange: () => {},
    id: null,
    isDisabled: false,
    isRequired: false,
    maxLength: null,
    minLength: null,
    pattern: null,
    placeholder: "",
    showErrorStyle: false,
    type: "text",
    usesCustomValue: false,
};

export default TextInput;
